import type { Faq } from "../seo/schema";
import { breadcrumbNode, faqPageNode, graph, serviceNode } from "../seo/schema";
import type { PageRoute } from "../seo/document";
import {
  accentRailMarkup,
  arrowList,
  ctaBand,
  faqSection,
  paragraphs,
  siteFooter,
  topNav,
} from "./layout";

type VerticalPageData = {
  path: string;
  keyword: string;
  shortName: string;
  h1: string;
  heroSub: string;
  whyPointOne: string;
  whyPointTwo: string;
  reviewBullets: readonly string[];
  auditBullets: readonly string[];
  chooserText: string;
  faqs: readonly Faq[];
  title: string;
  description: string;
  serviceType: string;
  secondaryHeroCta: string;
  secondaryHeroLabel: string;
  faqHeading: string;
  breadcrumbName: string;
  ctaHeadline: string;
  ctaBody: string;
};

function renderVerticalPage(data: VerticalPageData, assetBase: string): string {
  return `
    <main data-screen-label="${data.keyword}">
      ${topNav(assetBase)}

      <section class="section page-hero" data-screen-label="Hero">
        <div class="page-hero-inner">
          ${accentRailMarkup()}
          <p class="eyebrow">${data.keyword}</p>
          <h1>${data.h1}</h1>
          <p class="hero-subhead">${data.heroSub}</p>
          <div class="actions">
            <a class="button button-primary" href="/#services">See both monthly packs</a>
            <a class="button button-outline" href="${data.secondaryHeroCta}">${data.secondaryHeroLabel}</a>
          </div>
          <p class="proof-line">$149/month · No contracts · Cancel anytime</p>
        </div>
      </section>

      <section class="section" data-screen-label="Why it matters">
        <div class="section-heading">
          ${accentRailMarkup()}
          <h2>Why ${data.keyword} matters</h2>
          ${paragraphs([data.whyPointOne, data.whyPointTwo])}
        </div>
      </section>

      <section class="section section-soft" data-screen-label="What we do">
        <div class="section-heading">
          ${accentRailMarkup()}
          <h2>What Foxmark does for ${data.shortName} every month</h2>
        </div>
        <div class="service-duo">
          <div class="offer-block">
            <p class="block-label">
              <a href="/google-review-management">Google review and reputation management</a>
            </p>
            <ul>${arrowList(data.reviewBullets)}</ul>
          </div>
          <div class="offer-block">
            <p class="block-label"><a href="/website-audit-service">Monthly website audit service</a></p>
            <ul>${arrowList(data.auditBullets)}</ul>
          </div>
        </div>
      </section>

      <section class="section" data-screen-label="Pack chooser">
        <div class="section-heading">
          ${accentRailMarkup()}
          <h2>Which pack should you start with?</h2>
          <p>${data.chooserText}</p>
          <div class="actions">
            <a class="button button-primary" href="/google-review-management">Start review management</a>
            <a class="button button-outline" href="/website-audit-service">Start website audit</a>
          </div>
        </div>
      </section>

      ${faqSection(data.faqHeading, data.faqs)}

      ${ctaBand({
    headline: data.ctaHeadline,
    body: data.ctaBody,
    primaryHref: "/google-review-management",
    primaryLabel: "Start review management",
    secondaryHref: "/website-audit-service",
    secondaryLabel: "Start website audit",
    microcopy: "$149/month · Cancel anytime · No long-term contract",
  })}
    </main>
    ${siteFooter()}
  `;
}

const verticalPages: readonly VerticalPageData[] = [
  {
    path: "/reputation-management-for-dentists",
    keyword: "reputation management for dentists",
    shortName: "dental practices",
    h1: "Reputation management for dental practices",
    heroSub:
      "Reputation management for dentists drives trust before booking because patients read reviews and check your site. We keep review responses and the booking path clear every month.",
    whyPointOne:
      "Unanswered reviews make a practice look inactive and slow to care. Missing details, old hours, or broken links on your site make people think twice before booking.",
    whyPointTwo:
      "We treat reputation signals and website usability as one system. That lets you turn more site visitors into appointment requests with less friction.",
    reviewBullets: [
      "Respond to new reviews quickly in a consistent, practical tone",
      "Keep Google profile details current and accurate",
      "Surface positive themes so prospects can compare confidently",
      "Flag service issues so your front desk can answer faster",
    ],
    auditBullets: [
      "Audit booking flow for clarity on mobile and desktop",
      "Check service descriptions and office details for consistency",
      "Test contact points so patients can reach you in one step",
      "Review top-page layout and call-to-actions for action clarity",
    ],
    chooserText:
      "If you lose appointments to confusion or slow follow-up, start with review management. If your conversion path is the bigger leak, start with the website audit.",
    faqs: [
      {
        question: "How much does reputation management for dentists cost?",
        answer:
          "Flat rate is $149/month. It includes review management and the monthly website trust check, with no annual contract and cancel-anytime options.",
      },
      {
        question: "Do you respond to Google reviews for my dental practice?",
        answer:
          "Yes. Foxmark responds to reviews to show care continuity and keep your profile active while reducing manual load for your team.",
      },
      {
        question: "Can you work on both reviews and the practice website?",
        answer:
          "Yes. We map review updates against your site for consistency, and the two packs can be run in any order.",
      },
    ],
    title: "Reputation management for dentists support — $149/mo | Foxmark",
    description:
      "Reputation management for dentists and a monthly website trust audit keep your practice clear before booking. Flat $149/month for both monthly services.",
    serviceType: "reputation management for dentists",
    secondaryHeroCta: "/google-review-management",
    secondaryHeroLabel: "Start review management",
    faqHeading: "Dentists FAQ",
    breadcrumbName: "Dentists",
    ctaHeadline: "Protect your practice from lead leak",
    ctaBody:
      "Protect your Google presence, then tighten your website conversion path. Start with one focused monthly pack and add the other when you are ready.",
  },
  {
    path: "/local-seo-for-contractors",
    keyword: "local seo for contractors",
    shortName: "contractors and home service businesses",
    h1: "Local SEO for contractors and home service businesses",
    heroSub:
      "Local SEO for contractors drives more calls because homeowners read reviews and scan your site before contacting you. We fix profile details and quote paths every month.",
    whyPointOne:
      "For contractors, trust is fragile. If callers see unanswered reviews, outdated service pages, or hard-to-find contact details, they switch to the next option fast.",
    whyPointTwo:
      "Profile trust and site clarity work together. Strong review management plus a clear website conversion path increases the chance of turning searchers into estimate calls.",
    reviewBullets: [
      "Respond to reviews and customer questions quickly",
      "Keep service areas and categories updated on Google",
      "Post useful profile updates that match current work",
      "Turn recurring praise into clear social proof for leads",
    ],
    auditBullets: [
      "Check phone and quote contact clarity on top pages",
      "Audit how quickly a homeowner can request an estimate",
      "Find broken links and navigation friction on mobile",
      "Improve local landing page structure for conversion",
    ],
    chooserText:
      "If most leads disappear before the first call, start with review management. If calls come but estimates are weak to close, start with the website audit pack.",
    faqs: [
      {
        question: "How much does local seo for contractors cost?",
        answer:
          "Flat rate is $149/month. It includes review response and local profile care, plus a monthly website audit of your conversion paths.",
      },
      {
        question: "Will Foxmark fix missing phone and contact gaps?",
        answer:
          "Yes. Our monthly website audit checks contact visibility and quote request paths so homeowners can contact you without friction.",
      },
      {
        question: "Can I start one pack and add the other later?",
        answer:
          "Yes. Start with review management or website audit first. Both are $149/month and include cancel-anytime flexibility.",
      },
    ],
    title: "Local SEO for contractors services pack — $149/mo | Foxmark",
    description:
      "Local SEO for contractors keeps reviews, profile trust, and a monthly website audit in sync, helping calls convert. Flat $149/month for both monthly services.",
    serviceType: "local seo for contractors",
    secondaryHeroCta: "/google-review-management",
    secondaryHeroLabel: "Start review management",
    faqHeading: "Contractors FAQ",
    breadcrumbName: "Contractors",
    ctaHeadline: "Protect your lead flow from small leaks",
    ctaBody:
      "Fix trust signals and call flow together. Start with one monthly pack and add the other when conversion pressure appears.",
  },
  {
    path: "/marketing-for-med-spas",
    keyword: "marketing for med spas",
    shortName: "med spa owners",
    h1: "Marketing for med spas that turns reviews and web traffic into bookings",
    heroSub:
      "Marketing for med spas matters because clients compare trust signals before booking. We keep reviews, photos, and the booking path clear every month.",
    whyPointOne:
      "Unanswered reviews or mixed messaging can make a premium service look unreliable. A slow website path can turn a ready-to-book lead into a competitor's lead in one step.",
    whyPointTwo:
      "We align reputation work with site quality so prospects stay confident from first glance to booking confirmation.",
    reviewBullets: [
      "Respond to reviews with a tone that reflects premium service",
      "Keep Google profile details and hours current",
      "Promote trust points and service details consistently",
      "Turn repeated feedback into clearer service positioning",
    ],
    auditBullets: [
      "Audit booking pages for speed and clarity on mobile",
      "Review service pages for clear next steps",
      "Check visual treatment flow and page storytelling",
      "Repair navigation friction in discovery-to-booking paths",
    ],
    chooserText:
      "If reviews carry most of your inbound work, start with review management. If booking flow and service pages are weaker, start with the website audit pack.",
    faqs: [
      {
        question: "How much does marketing for med spas cost?",
        answer:
          "Flat rate is $149/month. It includes review/reputation management and a monthly website audit tailored for med spa conversions.",
      },
      {
        question: "Do you help with review content and reply tone?",
        answer:
          "Yes. Responses are practical and branded, and tuned for high-consideration services where trust is everything.",
      },
      {
        question: "How quickly do I see booking changes?",
        answer:
          "Most med spa pages improve in clarity within the first month as review updates and site fixes are applied and reflected publicly.",
      },
    ],
    title: "Marketing for med spas services pack — $149/mo | Foxmark",
    description:
      "Marketing for med spas pairs review care with a monthly website audit of booking pages, so prospects book with confidence. Flat $149/month for both services.",
    serviceType: "marketing for med spas",
    secondaryHeroCta: "/website-audit-service",
    secondaryHeroLabel: "Start website audit",
    faqHeading: "Med Spas FAQ",
    breadcrumbName: "Med Spas",
    ctaHeadline: "Turn trust into booked consultations",
    ctaBody:
      "Use your strongest signal first. Start review management if social proof is weak, or start website audit if your booking path leaks confidence.",
  },
  {
    path: "/seo-for-restaurants",
    keyword: "seo for restaurants",
    shortName: "restaurants",
    h1: "SEO for restaurants: reviews, profile, and website every month",
    heroSub:
      "SEO for restaurants matters because diners decide quickly from reviews, profile, and website clarity before they book. We keep those touchpoints tight every month.",
    whyPointOne:
      "For restaurants, search visibility, profile details, and page speed all affect same-day booking outcomes. Weak profile photos or unclear menus can reduce traffic and demand.",
    whyPointTwo:
      "We connect review reputation and site conversion so diners see clear proof and clear action steps when they are deciding for tonight or tonight's meal.",
    reviewBullets: [
      "Reply to reviews with practical, service-oriented responses",
      "Keep profile info, hours, and address details accurate",
      "Use review themes to reinforce what guests value most",
      "Keep listing trust signals synchronized with your website",
    ],
    auditBullets: [
      "Audit menu, reservation, and ordering paths for drop-offs",
      "Check schema and local profile signals for accuracy",
      "Test contact and location blocks for discoverability",
      "Reduce mobile friction at key booking and order steps",
    ],
    chooserText:
      "If most reservations come from social proof, start with review management. If traffic is there but reservations lag, start with the website audit pack.",
    faqs: [
      {
        question: "How much does SEO for restaurants cost?",
        answer:
          "Flat rate is $149/month. It covers review and profile work plus a monthly website audit of your booking and menu funnel.",
      },
      {
        question: "Can one pack fix both profile and website issues?",
        answer:
          "Each pack is focused for speed. Start with the pack that matches your current leak, then layer the other if needed.",
      },
      {
        question: "Is there a contract for this SEO service?",
        answer: "No contract needed. You can cancel anytime, month to month.",
      },
    ],
    title: "SEO for restaurants monthly services — $149/mo | Foxmark",
    description:
      "SEO for restaurants keeps reviews, profile, and a monthly website audit in sync so diners trust your next booking path. Flat $149/month for both monthly services.",
    serviceType: "seo for restaurants",
    secondaryHeroCta: "/website-audit-service",
    secondaryHeroLabel: "Start website audit",
    faqHeading: "Restaurants FAQ",
    breadcrumbName: "Restaurants",
    ctaHeadline: "Keep diners moving from search to reservation",
    ctaBody:
      "Start with the pack that matches your main leak. Then add the other if you want both trust and conversion systems updated every month.",
  },
];

export const verticalRoutes: PageRoute[] = verticalPages.map((vertical) => ({
  path: vertical.path,
  title: vertical.title,
  description: vertical.description,
  indexable: true,
  jsonLd: graph([
    serviceNode({
      name: vertical.h1,
      serviceType: vertical.serviceType,
      path: vertical.path,
      description: vertical.description,
    }),
    breadcrumbNode([
      { name: "Foxmark", path: "/" },
      { name: vertical.breadcrumbName, path: vertical.path },
    ]),
    faqPageNode(vertical.faqs),
  ]),
  render: (assetBase) => renderVerticalPage(vertical, assetBase),
}));
